import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Shield, Download, Trash2, Mail, ScanFace, ArrowLeft, AlertTriangle, Check } from "lucide-react";

const AI_KYC_PREANALYSIS_ENABLED = process.env.REACT_APP_ENABLE_AI_KYC_PREANALYSIS === "1";

export default function PrivacyAccount() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(user);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mml-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Vos données ont été exportées au format JSON");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const toggleMarketing = async () => {
    try {
      const { data } = await api.patch("/users/me/consents", { marketing_consent: !me?.marketing_consent });
      setMe({ ...me, marketing_consent: data.marketing_consent, marketing_consent_at: data.marketing_consent_at });
      toast.success(data.marketing_consent ? "Emails marketing activés" : "Emails marketing désactivés");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBiometric = async () => {
    try {
      const hasConsent = Boolean(me?.ai_biometric_consent_at);
      const { data } = await api.patch("/users/me/consents", { ai_biometric_consent: !hasConsent });
      setMe({ ...me, ai_biometric_consent_at: data.ai_biometric_consent_at });
      toast.success(data.ai_biometric_consent_at ? "Analyse IA biométrique autorisée" : "Consentement IA retiré");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (confirmText !== "SUPPRIMER") {
      toast.error("Tapez SUPPRIMER pour confirmer");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete("/users/me");
      toast.success(data.message, { duration: 8000 });
      await logout();
      nav("/");
    } catch (e) {
      toast.error(formatApiError(e), { duration: 6000 });
      setDeleting(false);
    }
  };

  if (!me) return <div className="max-w-3xl mx-auto px-6 py-12">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-account-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#1F4032] hover:underline mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>
      <div className="overline text-accent mb-2">— Mon compte</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Confidentialité &amp; RGPD</h1>
      <p className="text-neutral-600 mb-10">Vos droits sur vos données personnelles. Consultez aussi notre <Link to="/legal/privacy" className="text-[#1F4032] underline">politique de confidentialité</Link>.</p>

      {/* Consentement Marketing */}
      <div className="border border-[#1F4032]/10 bg-white p-5 rounded-md mb-4" data-testid="consent-marketing-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            <Mail className="w-5 h-5 text-[#1F4032] mt-1" />
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Emails marketing</div>
              <div className="text-sm text-neutral-600 mt-1 max-w-md">
                Recevoir des nouvelles, offres et bons plans MML. Les emails transactionnels (mission acceptée, paiement reçu…) restent envoyés dans tous les cas.
              </div>
              {me.marketing_consent && me.marketing_consent_at && (
                <div className="text-xs text-neutral-400 mt-1 font-mono">Activé le {me.marketing_consent_at.slice(0, 10)}</div>
              )}
            </div>
          </div>
          <Button
            onClick={toggleMarketing}
            variant={me.marketing_consent ? "outline" : "default"}
            className={`rounded-md gap-2 ${me.marketing_consent ? "border-emerald-600 text-emerald-700" : "bg-[#1F4032] text-white hover:bg-[#2A5443]"}`}
            data-testid="toggle-marketing-btn"
          >
            {me.marketing_consent ? <><Check className="w-4 h-4" /> Activé</> : "Activer"}
          </Button>
        </div>
      </div>

      {/* Cette option n'est affichée que si le traitement a été validé et activé. */}
      {AI_KYC_PREANALYSIS_ENABLED && <div className="border border-[#1F4032]/10 bg-white p-5 rounded-md mb-4" data-testid="consent-biometric-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            <ScanFace className="w-5 h-5 text-[#1F4032] mt-1" />
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Analyse IA des documents d'identité</div>
              <div className="text-sm text-neutral-600 mt-1 max-w-md">
                Pré-analyse facultative de la pièce d'identité et du selfie. Elle ne valide jamais le dossier : la décision reste prise par un membre habilité. Consultez la politique applicable pour connaître le prestataire et les durées de traitement.
              </div>
              {me.ai_biometric_consent_at && (
                <div className="text-xs text-neutral-400 mt-1 font-mono">Consenti le {me.ai_biometric_consent_at.slice(0, 10)}</div>
              )}
            </div>
          </div>
          <Button
            onClick={toggleBiometric}
            variant={me.ai_biometric_consent_at ? "outline" : "default"}
            className={`rounded-md gap-2 ${me.ai_biometric_consent_at ? "border-emerald-600 text-emerald-700" : "bg-[#1F4032] text-white hover:bg-[#2A5443]"}`}
            data-testid="toggle-biometric-btn"
          >
            {me.ai_biometric_consent_at ? <><Check className="w-4 h-4" /> Consenti</> : "Donner mon consentement"}
          </Button>
        </div>
      </div>}

      {/* Export */}
      <div className="border border-[#1F4032]/10 bg-white p-5 rounded-md mb-4" data-testid="export-data-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            <Download className="w-5 h-5 text-[#1F4032] mt-1" />
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Exporter mes données</div>
              <div className="text-sm text-neutral-600 mt-1 max-w-md">
                Téléchargez un fichier JSON contenant l'intégralité de vos données personnelles (Art. 15 &amp; 20 RGPD — droit d'accès &amp; portabilité).
              </div>
            </div>
          </div>
          <Button onClick={exportData} disabled={exporting} className="rounded-md gap-2 bg-[#1F4032] hover:bg-[#2A5443]" data-testid="export-data-btn">
            <Download className="w-4 h-4" /> {exporting ? "Génération..." : "Télécharger (JSON)"}
          </Button>
        </div>
      </div>

      {/* Suppression compte */}
      <div className="border border-red-200 bg-red-50/30 p-5 rounded-md" data-testid="delete-account-card">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-1 shrink-0" />
          <div>
            <div className="font-display font-bold text-lg tracking-tight text-red-900">Supprimer mon compte</div>
            <div className="text-sm text-red-800/80 mt-1 max-w-md">
              Action <strong>irréversible</strong>. Vos données personnelles seront supprimées (Art. 17 RGPD — droit à l'oubli). Les missions livrées et factures sont conservées sous forme anonymisée 10 ans (obligation comptable légale française).
            </div>
            <div className="text-xs text-red-700/70 mt-2">
              ⚠️ Impossible si vous avez des missions en cours, des litiges ouverts ou des paiements en attente.
            </div>
          </div>
        </div>
        {!confirmDelete ? (
          <Button
            onClick={() => setConfirmDelete(true)}
            variant="outline"
            className="rounded-md border-red-600 text-red-700 hover:bg-red-50 gap-2"
            data-testid="open-delete-confirm-btn"
          >
            <Trash2 className="w-4 h-4" /> Demander la suppression
          </Button>
        ) : (
          <div className="bg-white border border-red-300 rounded-md p-4 space-y-3">
            <div className="text-sm text-red-900">
              Tapez <strong className="font-mono bg-red-100 px-1.5 py-0.5 rounded">SUPPRIMER</strong> ci-dessous pour confirmer définitivement :
            </div>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="SUPPRIMER"
              className="w-full h-10 border border-red-300 rounded-md px-3 font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="delete-confirm-input"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => { setConfirmDelete(false); setConfirmText(""); }}
                className="rounded-md"
                data-testid="cancel-delete-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={doDelete}
                disabled={confirmText !== "SUPPRIMER" || deleting}
                className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4" /> {deleting ? "Suppression..." : "Supprimer définitivement"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 text-xs text-neutral-400 flex items-center gap-2">
        <Shield className="w-3 h-3" /> Pour exercer d'autres droits ou poser une question, utilisez le <Link to="/contact" className="underline">formulaire de contact</Link> jusqu'à la publication de l'adresse dédiée.
      </div>
    </div>
  );
}
